// The vital signs Wodi can record at triage (docs/plans/phase1-triage.md §2.1).
//
// Which vitals exist, their units and their precision are product scope, so they live
// here as constants. What counts as impossible, out of range or critical is clinical
// configuration held in the database and set by the clinical lead; no clinical number
// appears in this file. Codes (LOINC or other) are not here either: they are mapped per
// tenant from the loaded terminology (plan §2.2), because no licensed code is committed.
//
// Values are handled as scaled integers: the value times 10^decimals. A temperature of
// 37.2 °C is 372 with 1 decimal, so comparisons with limits and ranges are exact.

export const VITAL_KEYS = [
  'body_weight',
  'body_height',
  'body_temperature',
  'heart_rate',
  'bp_systolic',
  'bp_diastolic',
  'respiratory_rate',
  'oxygen_saturation',
  'pain_score',
  'muac',
  'blood_glucose',
] as const;
export type VitalKey = (typeof VITAL_KEYS)[number];

/** When a blood glucose was taken. TODO(clinical-verify): plan §13, C6. */
export const GLUCOSE_CONTEXTS = ['random', 'fasting'] as const;
export type GlucoseContext = (typeof GLUCOSE_CONTEXTS)[number];

export interface VitalDefinition {
  readonly key: VitalKey;
  /** Sentence-case label, as on the triage form. */
  readonly label: string;
  /** UCUM code, stored with every observation. */
  readonly unitCode: string;
  /** How the unit is shown inside the field. */
  readonly unitDisplay: string;
  /** Decimal places accepted and stored. Plan §13, P12. */
  readonly decimals: number;
  /** Position in the paper-chart order the triage form follows (DESIGN.md §8). */
  readonly order: number;
  /** The contexts a value may carry; empty when the vital has none. */
  readonly contexts: readonly string[];
}

export const VITAL_SIGNS: Readonly<Record<VitalKey, VitalDefinition>> = {
  body_weight: {
    key: 'body_weight',
    label: 'Weight',
    unitCode: 'kg',
    unitDisplay: 'kg',
    decimals: 2,
    order: 1,
    contexts: [],
  },
  body_height: {
    key: 'body_height',
    label: 'Height',
    unitCode: 'cm',
    unitDisplay: 'cm',
    decimals: 1,
    order: 2,
    contexts: [],
  },
  body_temperature: {
    key: 'body_temperature',
    label: 'Temperature',
    unitCode: 'Cel',
    unitDisplay: '°C',
    decimals: 1,
    order: 3,
    contexts: [],
  },
  heart_rate: {
    key: 'heart_rate',
    label: 'Pulse',
    unitCode: '/min',
    unitDisplay: 'beats/min',
    decimals: 0,
    order: 4,
    contexts: [],
  },
  bp_systolic: {
    key: 'bp_systolic',
    label: 'BP systolic',
    unitCode: 'mm[Hg]',
    unitDisplay: 'mmHg',
    decimals: 0,
    order: 5,
    contexts: [],
  },
  bp_diastolic: {
    key: 'bp_diastolic',
    label: 'BP diastolic',
    unitCode: 'mm[Hg]',
    unitDisplay: 'mmHg',
    decimals: 0,
    order: 6,
    contexts: [],
  },
  respiratory_rate: {
    key: 'respiratory_rate',
    label: 'Respiratory rate',
    unitCode: '/min',
    unitDisplay: 'breaths/min',
    decimals: 0,
    order: 7,
    contexts: [],
  },
  oxygen_saturation: {
    key: 'oxygen_saturation',
    label: 'SpO₂',
    unitCode: '%',
    unitDisplay: '%',
    decimals: 0,
    order: 8,
    contexts: [],
  },
  pain_score: {
    key: 'pain_score',
    label: 'Pain',
    unitCode: '{score}',
    unitDisplay: '0–10',
    decimals: 0,
    order: 9,
    contexts: [],
  },
  muac: {
    key: 'muac',
    label: 'MUAC',
    unitCode: 'cm',
    unitDisplay: 'cm',
    decimals: 1,
    order: 10,
    contexts: [],
  },
  blood_glucose: {
    key: 'blood_glucose',
    label: 'Blood glucose',
    unitCode: 'mmol/L',
    unitDisplay: 'mmol/L',
    decimals: 1,
    order: 11,
    contexts: GLUCOSE_CONTEXTS,
  },
};

/** The vitals in the order the triage form shows them. */
export const VITAL_SIGNS_IN_ORDER: readonly VitalDefinition[] = Object.values(VITAL_SIGNS).sort(
  (a, b) => a.order - b.order,
);

export function isVitalKey(value: string): value is VitalKey {
  return (VITAL_KEYS as readonly string[]).includes(value);
}

/** Whether `context` is allowed for the vital; `null` (no context) is always allowed. */
export function isVitalContextAllowed(key: VitalKey, context: string | null): boolean {
  return context === null || VITAL_SIGNS[key].contexts.includes(context);
}
